import React, { createContext, useContext, useState, useEffect } from 'react';
import enTranslations from '../translations/en.json';
import nlTranslations from '../translations/nl.json';

const TranslationContext = createContext();

const DEFAULT_LANGUAGE = 'nl';

const getStoredLanguage = () => localStorage.getItem('language') || DEFAULT_LANGUAGE;

export const useTranslation = () => {
  const context = useContext(TranslationContext);
  if (!context) {
    throw new Error('useTranslation must be used within a TranslationProvider');
  }
  return context;
};

export const TranslationProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => getStoredLanguage());

  // Initialize translations immediately based on language
  const [translations, setTranslations] = useState(() => {
    const initialLang = getStoredLanguage();
    return initialLang === 'nl' ? nlTranslations : enTranslations;
  });

  // Update translations when language changes
  useEffect(() => {
    // Use static imports instead of dynamic imports
    if (language === 'nl') {
      setTranslations(nlTranslations);
    } else {
      setTranslations(enTranslations);
    }
  }, [language]);

  const changeLanguage = (lang) => {
    setLanguage(lang);
    localStorage.setItem('language', lang);
  };

  const t = (key, params = {}) => {
    const { returnObjects, ...interpolation } = params;

    // If translations haven't loaded yet, return the key
    if (!translations || Object.keys(translations).length === 0) {
      return key;
    }
    
    // Handle nested keys like "landing.navFeatures"
    const keys = key.split('.');
    let translation = translations;
    
    // Traverse the nested object
    for (const k of keys) {
      if (translation && typeof translation === 'object' && k in translation) {
        translation = translation[k];
      } else {
        return key;
      }
    }
    
    if (returnObjects && (Array.isArray(translation) || (translation && typeof translation === 'object'))) {
      return translation;
    }

    // If translation is not a string, return the key
    if (typeof translation !== 'string') {
      return key;
    }
    
    // Replace parameters in translation string
    let result = translation;
    Object.keys(interpolation).forEach((param) => {
      result = result.replace(new RegExp(`{{${param}}}`, 'g'), interpolation[param]);
    });
    
    return result;
  };

  return (
    <TranslationContext.Provider value={{ language, changeLanguage, t }}>
      {children}
    </TranslationContext.Provider>
  );
};

